import React, { useState, useEffect, useMemo } from 'react';
import authApi, { CommunityAdminUser, CommunityUserStatsResponse } from '../../../services/authApi';
import './AdminUsersTab.css';

type Filter = 'ALL' | 'PREMIUM' | 'FREE' | 'EXPIRED_TODAY';

const AdminUsersTab: React.FC = () => {
  const [users, setUsers]   = useState<CommunityAdminUser[]>([]);
  const [stats, setStats]   = useState<CommunityUserStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [page, setPage]     = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authApi.adminGetCommunityUsers(),
      authApi.adminGetUserStats(),
    ])
      .then(([u, s]) => {
        setUsers(u.data ?? []);
        setStats(s.data ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const todayStr = new Date().toDateString();

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      if (filter === 'PREMIUM'      && !u.premium) return false;
      if (filter === 'FREE'         &&  u.premium) return false;
      if (filter === 'EXPIRED_TODAY') {
        if (!u.subExpiry) return false;
        if (new Date(u.subExpiry).toDateString() !== todayStr) return false;
      }
      if (!q) return true;
      return (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    });
  }, [users, search, filter, todayStr]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageUsers  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(0); };
  const handleFilter = (f: Filter) => { setFilter(f); setPage(0); };

  return (
    <div className="au-shell">
      {/* Header */}
      <div className="au-header">
        <div>
          <p className="au-eyebrow">Members</p>
          <h1 className="au-title">Community Users</h1>
        </div>
      </div>

      {/* Stats row */}
      <div className="au-stats-row">
        <StatCard label="Total Users"     value={stats?.totalUsers}   />
        <StatCard label="Premium"         value={stats?.premiumUsers} accent="amber" />
        <StatCard label="Free"            value={stats?.freeUsers}    />
        <StatCard label="New this month"  value={stats?.newThisMonth} accent="green" prefix="+" />
        <StatCard label="New this week"   value={stats?.newThisWeek}  accent="green" prefix="+" />
        <RevenueCard value={stats?.totalRevenue} />
      </div>

      {/* Toolbar */}
      <div className="au-toolbar">
        <input
          className="au-search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className="au-filter-tabs">
          <button className={`au-filter-btn ${filter === 'ALL' ? 'au-filter-btn--active' : ''}`} onClick={() => handleFilter('ALL')}>
            All ({users.length})
          </button>
          <button className={`au-filter-btn ${filter === 'PREMIUM' ? 'au-filter-btn--active' : ''}`} onClick={() => handleFilter('PREMIUM')}>
            ⚡ Premium ({stats?.premiumUsers ?? 0})
          </button>
          <button className={`au-filter-btn ${filter === 'FREE' ? 'au-filter-btn--active' : ''}`} onClick={() => handleFilter('FREE')}>
            Free ({stats?.freeUsers ?? 0})
          </button>
          <button
            className={`au-filter-btn au-filter-btn--danger ${filter === 'EXPIRED_TODAY' ? 'au-filter-btn--danger-active' : ''}`}
            onClick={() => handleFilter('EXPIRED_TODAY')}
          >
            🔴 Expired Today
            {(stats?.expiredToday ?? 0) > 0 && (
              <span className="au-expired-badge">{stats!.expiredToday}</span>
            )}
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="au-loading">Loading users…</div>
      ) : (
        <>
          <div className="au-table-wrap">
            <table className="au-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Provider</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Expiry</th>
                  <th>Paid (₹)</th>
                </tr>
              </thead>
              <tbody>
                {pageUsers.length === 0 ? (
                  <tr><td colSpan={7} className="au-empty">No users found</td></tr>
                ) : pageUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="au-user-cell">
                        <div className="au-avatar">{initial(u.name || u.email)}</div>
                        <div>
                          <div className="au-user-name">{u.name || '—'}</div>
                          <div className="au-user-email">{u.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`au-provider au-provider--${(u.authProvider || '').toLowerCase()}`}>
                        {u.authProvider === 'GOOGLE' ? '🌐 Google' : '📧 Email'}
                      </span>
                    </td>
                    <td className="au-date">{fmtDate(u.joinedAt)}</td>
                    <td>
                      {u.premium
                        ? <span className="au-badge au-badge--premium">⚡ Premium</span>
                        : <span className="au-badge au-badge--free">Free</span>
                      }
                    </td>
                    <td className="au-plan">{u.planName || '—'}</td>
                    <td className="au-date">
                      {u.subExpiry ? (
                        <span className={isExpiringSoon(u.subExpiry) ? 'au-expiry--warn' : ''}>
                          {fmtDate(u.subExpiry)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="au-amount">
                      {u.amountPaid != null ? `₹${Number(u.amountPaid).toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="au-pagination">
              <span className="au-page-info">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <button className="au-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button className="au-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUsersTab;

/* ── Helpers ──────────────────────────────────────────── */
function StatCard({ label, value, accent, prefix }: { label: string; value?: number; accent?: 'green' | 'amber'; prefix?: string }) {
  return (
    <div className="au-stat-card">
      <span className="au-stat-label">{label}</span>
      <span className={`au-stat-val ${accent ? `au-stat-val--${accent}` : ''}`}>
        {value != null ? `${prefix ?? ''}${value.toLocaleString()}` : '—'}
      </span>
    </div>
  );
}

function RevenueCard({ value }: { value?: number }) {
  const formatted = value != null
    ? `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';
  return (
    <div className="au-stat-card au-stat-card--revenue">
      <span className="au-stat-label">Total Revenue</span>
      <span className="au-stat-val au-stat-val--revenue">{formatted}</span>
      <span className="au-stat-sub">active subscriptions</span>
    </div>
  );
}

function initial(str: string | null | undefined): string {
  return (str || '?').charAt(0).toUpperCase();
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return s; }
}

function isExpiringSoon(expiry: string): boolean {
  try {
    const diff = new Date(expiry).getTime() - Date.now();
    return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}
